import { createAsyncThunk } from '@reduxjs/toolkit';

import { request } from '../../../utility/apiClients';
import { REDUCER_ENUMS } from '../../../enums/applicationEnums';
import { handleError } from '../../../utility/helpers';
import { APIS_ENDPOINTS, APIS_METHOD } from '../../../resources/api-constants';

export const signUpUser = createAsyncThunk(
    REDUCER_ENUMS.AUTH_SIGNUP,
    async (signUpData: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.SIGNUP, signUpData, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const verifyEmailOtp = createAsyncThunk(
    REDUCER_ENUMS.VERIFY_EMAIL,
    async (emailOtpData: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.VERIFY_EMAIL, emailOtpData, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const resendEmailOtp = createAsyncThunk(
    REDUCER_ENUMS.RESEND_EMAIL_OTP,
    async (email: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.RESEND_EMAIL_OTP, email, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const signInUser = createAsyncThunk(
    REDUCER_ENUMS.AUTH_USER_LOGIN,
    async (signInData: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.LOGIN, signInData, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const changePassword = createAsyncThunk(
    REDUCER_ENUMS.CHANGE_PASSWORD,
    async (reqObj: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.CHANGE_PASSWORD, reqObj, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const forgetPassword = createAsyncThunk(
    REDUCER_ENUMS.FORGET_PASSWORD,
    async (reqObj: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.FORGET_PASSWORD, reqObj, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const resetPassword = createAsyncThunk(
    REDUCER_ENUMS.RESET_PASSWORD,
    async (reqObj: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.RESET_PASSWORD, reqObj, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const verifyForgotPassword = createAsyncThunk(
    REDUCER_ENUMS.VERIFY_OTP,
    async (emailOtpData: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.VERIFY_OTP, emailOtpData, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const googleSignIn = createAsyncThunk(
    REDUCER_ENUMS.GOOGLE_SIGN_IN,
    async (reqObj: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.GOOGLE_SIGN_IN, reqObj, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const logout = createAsyncThunk(
    REDUCER_ENUMS.AUTH_LOGOUT,
    async (obj: any, {rejectWithValue}) => {
        try {
            // Only call logout API if token is available (to avoid 401 errors with expired tokens)
            // If token is expired/unavailable, we'll just clear local storage
            const { isTokenAvailable } = await import('../../../utility/helpers');
            if (!isTokenAvailable()) {
                // Token not available, skip API call - local cleanup will happen in userLogout
                // Return the same detail key the backend uses so UI messaging stays consistent
                return { detail: 'LOGOUT_SUCCESS' };
            }
            
            const response = await request(
                APIS_METHOD.POST,
                APIS_ENDPOINTS.AUTH_LOGOUT,
                obj,
                false
            );
            return response.data;
        } catch (err:any) {
            // If logout API fails (e.g., 401 with expired token), still return success
            // The userLogout function will handle local cleanup regardless
            if (err?.response?.status === 401) {
                // Return the standard success key so the toast message is user‑friendly
                return { detail: 'LOGOUT_SUCCESS' };
            }
            return handleError(err, rejectWithValue);
        }
    }
);

export const getAnnualRevenue = createAsyncThunk(
    REDUCER_ENUMS.ANNUAL_REVENUE,
    async (_, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.ANNUAL_REVENUE,
                {},
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getIndustryType = createAsyncThunk(
    REDUCER_ENUMS.INDUSTRY_TYPE,
    async (_, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.INDUSTRY_TYPE,
                {},
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getCompanySize = createAsyncThunk(
    REDUCER_ENUMS.COMPANY_SIZE,
    async (_, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                APIS_ENDPOINTS.COMPANY_SIZE,
                {},
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const addCompanyProfile = createAsyncThunk(
    REDUCER_ENUMS.COMPANY_PROFILE,
    async (profileObj: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.COMPANY_PROFILE, profileObj, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getUserProfile = createAsyncThunk(
    REDUCER_ENUMS.USER_PROFILE,
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                `${APIS_ENDPOINTS.COMPANY_PROFILE}/${payload.companyId}`,
                {},
                payload.loader
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const addJobInDraft = createAsyncThunk(
    'saved-job-draft',
    async (reqObj: any, {rejectWithValue}) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.JOB_SAVED_DRAFT, reqObj, false);
            return response.data;
        } catch (err:any) {
            return handleError(err, rejectWithValue);
        }
    },
);

